/**
 * Every path in the portal, in one place.
 *
 * A literal path written at a call site is a broken link waiting for a rename;
 * these constants are what `route.config.tsx`, `layout.config.tsx` and every
 * `<Link>` share. `as const` so a typo is a type error rather than a 404.
 */
import {
  type PaymentsListQuery,
  type VerificationQueue,
  type ViolationStatus,
} from '@aysar/contracts';

export const PUBLIC_ROUTES = {
  LOGIN: '/login',
} as const;

export const PRIVATE_ROUTES = {
  DASHBOARD: '/',
  MEMBERS: '/members',
  MEMBER_DETAIL: '/members/:id',
  ADMINS: '/admins',
  ADMINS_STAFF: '/admins/staff',
  ADMINS_STAFF_DETAIL: '/admins/staff/:id',
  ADMINS_ROLES: '/admins/roles',
  /**
   * The Trust queue — every identity collision the ban-evasion pipeline
   * recorded, flagged and blocked alike (P14b).
   */
  TRUST: '/trust',
  /**
   * The per-signal policy editor, on its own path rather than an anchor on the
   * queue. `settingOwnerFor('trust')` links here, and P10.5d2's lesson is why it
   * is a route: a link that lands an operator at the top of a page and leaves
   * them to find the control is barely better than saying the editor is not here.
   */
  TRUST_POLICY: '/trust/policy',
  /**
   * The verification review queue — every member waiting for their identity to
   * be checked (P15b).
   */
  VERIFICATION: '/verification',
  /**
   * The interview availability calendar — every window staff have published for
   * members to book into (D9b1).
   *
   * Declared **before** `/verification/:id` and matched before it, the way
   * `VENUE_NEW` sits ahead of `VENUE_DETAIL`: a literal segment beside a param
   * route is only a literal if it is listed first, and `:id` would otherwise
   * swallow `interviews` and send an operator to a case that does not exist.
   *
   * A route rather than a tab on the queue: it answers a different question
   * (what are we offering, to whom) with a different shape, and an operator
   * publishing a term's availability wants to be able to link a colleague to it.
   */
  VERIFICATION_INTERVIEWS: '/verification/interviews',
  /**
   * One case: the evidence, the interview, the advisory and the verdict.
   *
   * A PAGE rather than a dialog, and not because it is large. The case is what a
   * reviewer reads for minutes and links a colleague to, its evidence is a set
   * of audited mints (N12) that must not fire from a list, and a decision taken
   * inside an overlay over the queue would be taken with the queue's own
   * polling refresh running underneath it.
   */
  VERIFICATION_CASE: '/verification/:id',
  /**
   * The reports queue — every member another member has complained about (P19b).
   *
   * One route and no detail path: a report is decided in a dialog, because there
   * is nothing on it a page would add. It carries no evidence to mint, no
   * photograph and no conversation to compose (`Report` carries no member
   * content, by design), so a second layout would cost the queue's context and
   * buy a URL.
   */
  MODERATION: '/moderation',
  /**
   * The AI-moderation queue — every message the classifier flagged that a human
   * has not yet decided (P20b).
   *
   * One route and no detail path, for the reason `/moderation` gives: the case
   * is decided in a dialog. The evidence is a single message body, not a set of
   * audited media mints, and a page would cost the queue's context to buy a URL.
   */
  VIOLATIONS: '/violations',
  /**
   * The rule catalogue — on its own path rather than an anchor on the queue.
   *
   * A different permission (`violations.manage` for its reads too, because the
   * guidance is the classifier's prompt) and a different job: the queue is work,
   * this is policy. P10.5d2's lesson is why it is a route — a link that lands an
   * operator at the top of a page and leaves them to find the control is barely
   * better than saying the editor is not here.
   */
  VIOLATION_TYPES: '/violations/types',
  /**
   * The notification copy — the module's home (P21b).
   *
   * There is deliberately **no bare `/notifications`**: the nav parent points
   * here, exactly as the Trust and Violations parents point at their queues, and
   * a `/notifications/:id` at the module root would sit one static segment away
   * from `/notifications/deliveries` for no gain.
   */
  NOTIFICATION_TEMPLATES: '/notifications/templates',
  /**
   * The editor — a PAGE, not a dialog.
   *
   * `new` is a literal segment and must not be readable as a template id;
   * react-router ranks a static segment above a dynamic one, so the two coexist
   * whichever order they are registered in, and they are listed with `new` first
   * anyway so the next reader does not have to know that rule. It is a page
   * because the live preview beside the form has to show the same content
   * language as the form's own tab — the `locale`/`onLocaleChange` seam
   * `DynamicForm` grew for the question editor — and a preview panel does not
   * fit beside the fields in a dialog.
   */
  NOTIFICATION_TEMPLATE_NEW: '/notifications/templates/new',
  NOTIFICATION_TEMPLATE_DETAIL: '/notifications/templates/:id',
  /** The delivery log and its dead letters — a work list, so it polls (W5). */
  NOTIFICATION_DELIVERIES: '/notifications/deliveries',
  /**
   * The per-category × per-channel defaults grid — the editor
   * `settingOwnerFor('notifications')` links to, on its own path rather than an
   * anchor, for the reason `TRUST_POLICY` gives: the editor is the whole page.
   */
  NOTIFICATION_DEFAULTS: '/notifications/defaults',
  /** Composed announcements and their walks (P21c). */
  NOTIFICATION_CAMPAIGNS: '/notifications/campaigns',
  /**
   * The composer — a PAGE, for the template editor's reason and one more.
   *
   * The preview beside the form is not a rendering of the copy but a **count and
   * a sample of who will receive it**, which is the thing a human has to see
   * before an irreversible send. That panel does not fit beside the fields in a
   * dialog, and putting it behind a second click would defeat the point of it.
   */
  NOTIFICATION_CAMPAIGN_NEW: '/notifications/campaigns/new',
  NOTIFICATION_CAMPAIGN_DETAIL: '/notifications/campaigns/:id',
  /**
   * The curriculum — every course the platform teaches (P22b).
   *
   * A bare `/courses` here, unlike `/notifications`, because the course list IS
   * the module's front door: discount rules and attempts (P22b3) are reached
   * *through* a course before they are reached beside it. Tests are the one
   * exception, and `TESTS` says why.
   */
  COURSES: '/courses',
  /**
   * The course editor — a PAGE, not a dialog.
   *
   * The lesson body is a block document edited one content-language at a time,
   * and the locale strip above it has to govern the lesson rail beside it as
   * well — the `locale`/`onLocaleChange` seam `DynamicForm` grew for the question
   * editor. Neither region fits beside the other in an overlay.
   *
   * `new` is a literal segment and must not be readable as a course id;
   * react-router ranks a static segment above a dynamic one, so the two coexist
   * whichever order they are registered in, and they are listed with `new` first
   * anyway so the next reader does not have to know that rule.
   */
  COURSE_NEW: '/courses/new',
  COURSE_DETAIL: '/courses/:id',
  /**
   * Every test, beside the courses rather than only inside them (P22b2).
   *
   * A list of its own because a **standalone** test has no course to be reached
   * through: `tests.courseId` is nullable by design (matrix row 4), and a portal
   * that can create one it cannot then find is unfinished. A course's own test
   * is still reached from the course, which is where an author looks for it.
   */
  TESTS: '/tests',
  /** `new` is a literal segment, on `COURSE_NEW`'s terms. */
  TEST_NEW: '/tests/new',
  TEST_DETAIL: '/tests/:id',
  /**
   * What finishing a course or passing a test is worth (P22b3).
   *
   * `COURSES`' comment said rules are reached *through* a course, and that is
   * still where an author starts — the course page and the test page each list
   * their own. It is not where a rule can be **found**, though: a rule names a
   * course *or* a test, so a portal with no list of its own can show an operator
   * every rule that pays for a course and none of the ones that pay for a
   * standalone test. That is the same asymmetry `TESTS` was added for.
   */
  DISCOUNT_RULES: '/discount-rules',
  /** `new` is a literal segment, on `COURSE_NEW`'s terms. */
  DISCOUNT_RULE_NEW: '/discount-rules/new',
  DISCOUNT_RULE_DETAIL: '/discount-rules/:id',
  /**
   * What members have actually earned, and how (P22b4) — the support surface.
   *
   * **One route for two lists**, with the tab in the query string beside the
   * filters. The question this screen answers is "why has this member not got
   * their discount?", and it is answered by looking at their grants *and* their
   * attempts — so the two share a screen and a member filter rather than
   * sitting under two nav entries an operator has to bounce between.
   *
   * Named for the member rather than for the tables (`/grants`, `/attempts`)
   * because the member is what an operator arrives holding.
   */
  MEMBER_DISCOUNTS: '/member-discounts',
  /**
   * What the platform sells — the subscription catalogue (P23b1).
   *
   * The module is called `Subscriptions` and the settings owner is called
   * `billing`, because one is the admin's word for the surface and the other is
   * the domain's; `settingOwnerFor` is where the two meet.
   */
  SUBSCRIPTIONS: '/subscriptions',
  /** `new` is a literal segment, on `COURSE_NEW`'s terms. */
  SUBSCRIPTION_NEW: '/subscriptions/new',
  /**
   * How an earned discount reaches an in-app purchase (P23b3c).
   *
   * Under `subscriptions` rather than `payments`: `Payments` is the *direct*
   * channel, while this is the IAP counterpart of the price board — the same
   * question as "what does this plan cost", asked of a store we do not control.
   *
   * Listed before `SUBSCRIPTION_DETAIL` for the next reader; react-router ranks
   * a static segment above a dynamic one whichever order they appear in.
   */
  OFFER_CODES: '/subscriptions/offer-codes',
  SUBSCRIPTION_DETAIL: '/subscriptions/:id',
  /**
   * What each plan costs, and what a subscription unlocks (P23b1).
   *
   * A screen of its own rather than a card on the catalogue, and it is the
   * destination `settingOwnerFor('billing')` hands off to. Three reasons, in
   * order: a price is a **setting** and needs `settings.read` where the
   * catalogue needs only `billing.read`, so one page would be half-restricted
   * for a real operator; the board compares plans against each other, which is
   * the comparison an operator opens it to make; and `SettingOwner.to` allows
   * ONE destination per owner while `billing` owns three keys — a page that *is*
   * the editor needs no `?setting=` anchor, which is the shape `TRUST_POLICY`
   * and `NOTIFICATION_DEFAULTS` already use. P26's tier matrix lands here too,
   * with no second flip.
   */
  SUBSCRIPTION_PRICING: '/subscriptions/pricing',
  /**
   * The staff-managed payment channel — every order somebody paid off-platform
   * (P23b2).
   *
   * A surface of its own rather than a tab on the catalogue: the catalogue
   * answers "what do we sell", and this answers "who paid, and did they get it".
   * They share a permission (`billing.read`) and nothing else — the queue is a
   * working list an operator lives in, and the catalogue is a thing they edit
   * twice a year.
   */
  PAYMENTS: '/payments',
  /** `new` is a literal segment, on `SUBSCRIPTION_NEW`'s terms. */
  PAYMENT_NEW: '/payments/new',
  /**
   * The store channel's trail (P23b3a) — the other half of "who paid, and did
   * they get it".
   *
   * A literal segment under `/payments`, on `PAYMENT_NEW`'s terms, and it must
   * stay ahead of `PAYMENT_DETAIL` in the reader's mind even though react-router
   * ranks a static segment above a dynamic one either way.
   *
   * A child of payments rather than a surface of its own: the two answer the
   * same question from the two channels — one is what a human recorded, the
   * other is what a store sent — and they share `billing.read`.
   */
  BILLING_EVENTS: '/payments/events',
  /**
   * One member's entitlements (P23b3b) — the third literal segment under
   * `/payments`, on `PAYMENT_NEW`'s terms.
   *
   * A child of payments rather than of members: what it holds is commercial
   * access, read by `billing.read`, and the questions it answers ("why does this
   * account have premium?", "who cancelled this?") arrive from the two payment
   * channels beside it rather than from a profile.
   *
   * The member travels as a **query** parameter and not a path segment, because
   * the screen is reachable with nobody chosen — that is its resting state, not
   * a 404 — and because `useListUrlState` is what carries it, so the view stays
   * something an operator can bookmark and paste.
   */
  ENTITLEMENTS: '/payments/entitlements',
  /**
   * The ledger (P31b) — the fourth literal segment under `/payments`, on
   * `BILLING_EVENTS`' terms, and ahead of `PAYMENT_DETAIL` for the reader.
   *
   * One row per money movement from **every** channel, where the queue is the
   * direct channel's work list and the trail is what a store sent. It sits
   * beside them rather than replacing either: the ledger says what money did,
   * and every act on that money stays on the order and entitlement pages.
   */
  LEDGER: '/payments/ledger',
  PAYMENT_DETAIL: '/payments/:id',
  SETTINGS: '/settings',
  SETTINGS_GROUP: '/settings/:group',
  INTEGRATIONS: '/integrations',
  QUESTIONS: '/questions',
  /**
   * The question editor — a PAGE since P10.6d, not a dialog.
   *
   * `new` is a literal segment and must not be readable as a question id;
   * react-router ranks a static segment above a dynamic one, so the two coexist
   * whichever order they are registered in, and they are listed with `new` first
   * anyway so the next reader does not have to know that rule.
   */
  QUESTION_NEW: '/questions/new',
  QUESTION_DETAIL: '/questions/:id',
  /**
   * Legal documents and their versions (P7b). `versions` is a literal segment,
   * so `/legal/versions/:id` outranks `/legal/:id` on `QUESTION_NEW`'s terms.
   */
  LEGAL: '/legal',
  LEGAL_VERSION_NEW: '/legal/:documentId/versions/new',
  LEGAL_VERSION_DETAIL: '/legal/versions/:id',
  LEGAL_VERSION_CONSENTS: '/legal/versions/:id/consents',
  LEGAL_DOCUMENT_DETAIL: '/legal/:id',
  /**
   * The Overlay Lab — a development-only workbench that mounts every overlay in
   * the kit with over-tall content (P10.1). `route.config.tsx` registers it under
   * `import.meta.env.DEV`, so in a production build the path resolves to the
   * catch-all like any other unknown one.
   */
  KIT_LAB: '/kit',
  /**
   * The Selection Lab — every selection control the kit ships, in every state
   * (P10.2a). Development-only on the same terms as `KIT_LAB`.
   */
  SELECTION_LAB: '/kit/selection',
  /**
   * The Structured Lab — the key/value and JSON editors, in every state
   * (P10.2b). Development-only on the same terms as `KIT_LAB`.
   */
  STRUCTURED_LAB: '/kit/structured',
  /**
   * The Scalar Lab — duration, percent and date-format inputs in every state
   * (P10.2c). Development-only on the same terms as `KIT_LAB`.
   */
  SCALAR_LAB: '/kit/scalar',
  /**
   * The Form Lab — one form in five boxes of different widths, so the
   * container-driven column model can be measured at a single viewport
   * (P10.2d). Development-only on the same terms as `KIT_LAB`.
   */
  FORM_LAB: '/kit/form',
  /**
   * The Media Lab — the picker in every state it has, over an in-memory source
   * so the page touches no api (P10.2f). Development-only on the same terms as
   * `KIT_LAB`.
   */
  MEDIA_LAB: '/kit/media',
  /**
   * The Calendar Lab — the month grid in every state it has (P25b1).
   * Development-only on the same terms as `KIT_LAB`.
   */
  CALENDAR_LAB: '/kit/calendar',
  /**
   * Staff oversight of every booking on the platform (P25d2) — the Meetings
   * module's front door, which is why it holds the bare `/meetings`.
   *
   * The address `VENUES` reserved two sub-phases ago, now taken. Venues sit
   * **beside** it rather than under it, the way `/tests` sits beside `/courses`.
   */
  MEETINGS: '/meetings',
  /**
   * One booking, and everything staff may do to it.
   *
   * No `new` sibling to rank against, unlike `VENUE_DETAIL`: a booking is made
   * by two members agreeing, and there is no staff route that creates one.
   */
  MEETING_BOOKING_DETAIL: '/meetings/:id',
  /**
   * The partner venue directory (P25a1).
   *
   * A bare `/venues` rather than `/meetings/venues`: the booking oversight above
   * is the module's front door at `/meetings`, and venues sit **beside** it the
   * way `/tests` sits beside `/courses` rather than nesting under it. Choosing
   * that in P25a1 is what let P25d2 take `/meetings` without renaming a live
   * address.
   */
  VENUES: '/venues',
  /**
   * The venue editor — a PAGE, not a dialog. Four sections across two locales
   * plus a contact block does not fit an overlay.
   *
   * `new` is a literal segment and must not be readable as a venue id;
   * react-router ranks a static segment above a dynamic one, so the two coexist
   * whichever order they are registered in, and they are listed with `new` first
   * anyway so the next reader does not have to know that rule.
   */
  VENUE_NEW: '/venues/new',
  VENUE_DETAIL: '/venues/:id',
  /**
   * One venue's availability calendar (P25b2).
   *
   * A route **under** the editor rather than a section inside it, decided in
   * P25b1 and settled here: `/venues/new` has no venue to schedule, so a card on
   * the editor would need a fourth "save first" state that a route makes
   * structurally impossible; the displayed month belongs in the URL, so a
   * colleague can be sent the week in question; and the editor is already four
   * sections across two locales plus a photo gallery.
   *
   * Read by `meetings.read` — the support desk has a legitimate reason to see
   * when a venue is open — while every write inside it is `meetings.manage`.
   */
  VENUE_AVAILABILITY: '/venues/:id/availability',
} as const;

/**
 * The one place a parameterised path is filled in.
 *
 * A `<Link to={`/admins/staff/${id}`}>` written at a call site is the broken link
 * a rename produces; this keeps the pattern and its uses in step.
 */
export function staffDetailPath(userId: string): string {
  return PRIVATE_ROUTES.ADMINS_STAFF_DETAIL.replace(':id', encodeURIComponent(userId));
}

export function memberDetailPath(userId: string): string {
  return PRIVATE_ROUTES.MEMBER_DETAIL.replace(':id', encodeURIComponent(userId));
}

export function verificationCasePath(caseId: string): string {
  return PRIVATE_ROUTES.VERIFICATION_CASE.replace(':id', encodeURIComponent(caseId));
}

/** The queue tab's URL key — the queue hook reads it, the dashboard tiles write it. */
export const VERIFICATION_QUEUE_PARAM = 'queue';

/**
 * The review queue opened on one work list — what the dashboard tile counted is
 * exactly what it opens. `VERIFICATION_INTERVIEWS` is the availability calendar,
 * not the interviews waiting to be held.
 */
export function verificationQueuePath(queue: VerificationQueue): string {
  return `${PRIVATE_ROUTES.VERIFICATION}?${VERIFICATION_QUEUE_PARAM}=${queue}`;
}

/**
 * The violations queue cut to one status. A bare `/violations` is the whole open
 * set (queued AND appealed), so a tile that counted one of them must say which.
 */
export function violationsQueuePath(status: ViolationStatus): string {
  return `${PRIVATE_ROUTES.VIOLATIONS}?status=${status}`;
}

export function settingsGroupPath(group: string): string {
  return PRIVATE_ROUTES.SETTINGS_GROUP.replace(':group', encodeURIComponent(group));
}

export function questionEditorPath(questionId: string): string {
  return PRIVATE_ROUTES.QUESTION_DETAIL.replace(':id', encodeURIComponent(questionId));
}

export function legalDocumentPath(documentId: string): string {
  return PRIVATE_ROUTES.LEGAL_DOCUMENT_DETAIL.replace(':id', encodeURIComponent(documentId));
}

export function newLegalVersionPath(documentId: string): string {
  return PRIVATE_ROUTES.LEGAL_VERSION_NEW.replace(':documentId', encodeURIComponent(documentId));
}

export function legalVersionPath(versionId: string): string {
  return PRIVATE_ROUTES.LEGAL_VERSION_DETAIL.replace(':id', encodeURIComponent(versionId));
}

export function legalVersionConsentsPath(versionId: string): string {
  return PRIVATE_ROUTES.LEGAL_VERSION_CONSENTS.replace(':id', encodeURIComponent(versionId));
}

/**
 * The create route, carrying the section the question is being added to.
 *
 * A query parameter rather than a second path segment: the section is context
 * for a new question, not part of its address, and `/questions/new/:sectionId`
 * would be a URL with no meaning once the question exists.
 */
export function newQuestionPath(sectionId: string): string {
  return `${PRIVATE_ROUTES.QUESTION_NEW}?${NEW_QUESTION_SECTION_PARAM}=${encodeURIComponent(sectionId)}`;
}

export const NEW_QUESTION_SECTION_PARAM = 'section';

export function templateEditorPath(templateId: string): string {
  return PRIVATE_ROUTES.NOTIFICATION_TEMPLATE_DETAIL.replace(':id', encodeURIComponent(templateId));
}

/**
 * The create route, optionally carrying the notice it is being written for.
 *
 * Query parameters rather than path segments, for the reason `newQuestionPath`
 * gives: the key and the channel are context for a template that does not exist
 * yet, not part of its address — and they are in the URL at all so the "write
 * it" shortcut from the unauthored list survives a refresh.
 */
export function newTemplatePath(key?: string, channel?: string): string {
  const params = new URLSearchParams();
  if (key !== undefined) params.set(NEW_TEMPLATE_KEY_PARAM, key);
  if (channel !== undefined) params.set(NEW_TEMPLATE_CHANNEL_PARAM, channel);

  const query = params.toString();

  return query === ''
    ? PRIVATE_ROUTES.NOTIFICATION_TEMPLATE_NEW
    : `${PRIVATE_ROUTES.NOTIFICATION_TEMPLATE_NEW}?${query}`;
}

export function courseDetailPath(courseId: string): string {
  return PRIVATE_ROUTES.COURSE_DETAIL.replace(':id', encodeURIComponent(courseId));
}

export function bookingDetailPath(bookingId: string): string {
  return PRIVATE_ROUTES.MEETING_BOOKING_DETAIL.replace(':id', encodeURIComponent(bookingId));
}

export function venueDetailPath(venueId: string): string {
  return PRIVATE_ROUTES.VENUE_DETAIL.replace(':id', encodeURIComponent(venueId));
}

export function venueAvailabilityPath(venueId: string): string {
  return PRIVATE_ROUTES.VENUE_AVAILABILITY.replace(':id', encodeURIComponent(venueId));
}

/**
 * A course's detail, opened straight onto one of its lessons.
 *
 * A query parameter rather than a path segment, for the reason
 * `newQuestionPath` gives: which lesson is being edited is *where the operator
 * is inside the page*, not part of the course's address — and it is in the URL
 * at all so a half-written lesson survives a refresh and can be sent to a
 * colleague.
 */
export function courseLessonPath(courseId: string, lessonId: string): string {
  return `${courseDetailPath(courseId)}?${COURSE_LESSON_PARAM}=${encodeURIComponent(lessonId)}`;
}

export const COURSE_LESSON_PARAM = 'lesson';

/** The value `COURSE_LESSON_PARAM` carries while a lesson is being created. */
export const NEW_LESSON_ID = 'new';

export function testDetailPath(testId: string): string {
  return PRIVATE_ROUTES.TEST_DETAIL.replace(':id', encodeURIComponent(testId));
}

/**
 * The create route, carrying the course the test is being written for.
 *
 * A query parameter, for `newQuestionPath`'s reason: the course is context for a
 * test that does not exist yet, and `courseId` is settable on **create only**
 * (`CourseTestUpdateSchema` does not carry it), so it is never part of the
 * saved test's address.
 */
export function newTestPath(courseId?: string): string {
  return courseId === undefined
    ? PRIVATE_ROUTES.TEST_NEW
    : `${PRIVATE_ROUTES.TEST_NEW}?${NEW_TEST_COURSE_PARAM}=${encodeURIComponent(courseId)}`;
}

export const NEW_TEST_COURSE_PARAM = 'courseId';

/** A test's detail, opened straight onto one of its questions. */
export function testQuestionPath(testId: string, questionId: string): string {
  return `${testDetailPath(testId)}?${TEST_QUESTION_PARAM}=${encodeURIComponent(questionId)}`;
}

export const TEST_QUESTION_PARAM = 'question';

/** The value `TEST_QUESTION_PARAM` carries while a question is being written. */
export const NEW_QUESTION_ID = 'new';

export function discountRulePath(ruleId: string): string {
  return PRIVATE_ROUTES.DISCOUNT_RULE_DETAIL.replace(':id', encodeURIComponent(ruleId));
}

/**
 * The create route, carrying what the rule will pay for.
 *
 * Query parameters, for `newTestPath`'s reason and one sharper: `source` and
 * `sourceId` are settable on **create only** — `DiscountRuleUpdateSchema`
 * carries neither — so they are context for a rule that does not exist yet and
 * never part of a saved rule's address. This is what lets a course's own panel
 * offer "what is finishing this worth?" without a second create screen.
 */
export function newDiscountRulePath(seed?: { source: string; sourceId: string }): string {
  if (seed === undefined) {
    return PRIVATE_ROUTES.DISCOUNT_RULE_NEW;
  }

  const query = new URLSearchParams({
    [DISCOUNT_RULE_SOURCE_PARAM]: seed.source,
    [DISCOUNT_RULE_SOURCE_ID_PARAM]: seed.sourceId,
  });

  return `${PRIVATE_ROUTES.DISCOUNT_RULE_NEW}?${query.toString()}`;
}

export const DISCOUNT_RULE_SOURCE_PARAM = 'source';
export const DISCOUNT_RULE_SOURCE_ID_PARAM = 'sourceId';

export function campaignEditorPath(campaignId: string): string {
  return PRIVATE_ROUTES.NOTIFICATION_CAMPAIGN_DETAIL.replace(':id', encodeURIComponent(campaignId));
}

/** The delivery log, narrowed to one campaign's own deliveries. */
export function campaignDeliveriesPath(campaignId: string): string {
  return `${PRIVATE_ROUTES.NOTIFICATION_DELIVERIES}?campaignId=${encodeURIComponent(campaignId)}`;
}

export function subscriptionDetailPath(productId: string): string {
  return PRIVATE_ROUTES.SUBSCRIPTION_DETAIL.replace(':id', encodeURIComponent(productId));
}

/**
 * The queue narrowed to what members sent from the app and nobody has decided
 * on (F3) — the list the sidebar badge and the dashboard tile are counted
 * from, so what they open is exactly what they counted. The keys are the
 * queue's own URL filters (`useListUrlState`), never a second vocabulary; the
 * lookup that counts them reads this pair, so the two cannot drift.
 */
export const AWAITING_RECEIPTS_FILTERS = {
  origin: 'member',
  status: 'awaiting_confirmation',
} as const;

export function paymentsAwaitingReviewPath(): string {
  return `${PRIVATE_ROUTES.PAYMENTS}?${new URLSearchParams(AWAITING_RECEIPTS_FILTERS).toString()}`;
}

/** One payment order — the terms, the timeline, and the five decisions. */
export function paymentDetailPath(orderId: string): string {
  return PRIVATE_ROUTES.PAYMENT_DETAIL.replace(':id', encodeURIComponent(orderId));
}

/** The query parameters `/payments/new` seeds its two pickers from. */
export const NEW_PAYMENT_USER_PARAM = 'userId';
export const NEW_PAYMENT_PRODUCT_PARAM = 'productKey';

/**
 * The order desk, opened on a member — and on a product when the caller knows
 * which one (D9b2: the verification case page sends the reviewer here to record
 * an interview fee). Another module's door into Payments: a link, never an
 * import (W6). `newDiscountRulePath`'s shape.
 */
export function newPaymentPath(seed?: { userId: string; productKey?: string }): string {
  if (seed === undefined) {
    return PRIVATE_ROUTES.PAYMENT_NEW;
  }

  const query = new URLSearchParams({ [NEW_PAYMENT_USER_PARAM]: seed.userId });
  if (seed.productKey !== undefined) {
    query.set(NEW_PAYMENT_PRODUCT_PARAM, seed.productKey);
  }

  return `${PRIVATE_ROUTES.PAYMENT_NEW}?${query.toString()}`;
}

/** The query parameter `/payments/entitlements` reads its member from. */
export const ENTITLEMENTS_MEMBER_PARAM = 'userId';

/**
 * One member's entitlements, opened on that member (P23b3b).
 *
 * This is the **read-only way in** for an operator without `members.read`: the
 * screen's picker is withheld from them (P21b), so a link that carries the
 * account is what keeps the page usable — and it needs no permission of its own,
 * because it narrows by an id already on the row somebody is looking at.
 */
export function memberEntitlementsPath(userId: string): string {
  return `${PRIVATE_ROUTES.ENTITLEMENTS}?${ENTITLEMENTS_MEMBER_PARAM}=${encodeURIComponent(userId)}`;
}

/**
 * The ledger's own filters, as another surface may pre-set them (P31b).
 *
 * The keys are the ones `PaymentsListQuerySchema` names and `LedgerPage` lists in
 * its `filterKeys` — both, or the link is a painted door (P27c2): a key the api
 * refuses answers 422, and a key the page does not read is silently ignored. The
 * date bounds are deliberately absent, because the URL carries them as day keys
 * while the contract wants instants, and a caller would have to know which.
 */
export type LedgerLinkFilters = Partial<
  Pick<PaymentsListQuery, 'userId' | 'store' | 'status' | 'kind' | 'source' | 'externalRef'>
>;

export function ledgerPath(filters: LedgerLinkFilters = {}): string {
  const query = new URLSearchParams();

  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') {
      query.set(key, value);
    }
  }

  const search = query.toString();

  return search === '' ? PRIVATE_ROUTES.LEDGER : `${PRIVATE_ROUTES.LEDGER}?${search}`;
}

/** The query parameter `/payments/ledger` opens its row sheet from. */
export const LEDGER_PAYMENT_PARAM = 'payment';

/**
 * The ledger with one row's sheet already open — a link a dispute thread can
 * carry. A query parameter for `courseLessonPath`'s reason: which row is open is
 * where the operator is inside the ledger, not part of the ledger's address.
 */
export function ledgerPaymentPath(paymentId: string): string {
  return `${PRIVATE_ROUTES.LEDGER}?${LEDGER_PAYMENT_PARAM}=${encodeURIComponent(paymentId)}`;
}

/**
 * The pricing board, opened with one plan's editor already up.
 *
 * A query parameter rather than a path segment, for `courseLessonPath`'s reason:
 * which plan is being priced is *where the operator is inside the board*, not
 * part of the board's address — and it is in the URL so the product page can
 * hand off to the exact row rather than to the top of a table.
 */
export function subscriptionPricingPath(productKey?: string): string {
  return productKey === undefined
    ? PRIVATE_ROUTES.SUBSCRIPTION_PRICING
    : `${PRIVATE_ROUTES.SUBSCRIPTION_PRICING}?${PRICING_PRODUCT_PARAM}=${encodeURIComponent(productKey)}`;
}

export const PRICING_PRODUCT_PARAM = 'plan';

export const NEW_TEMPLATE_KEY_PARAM = 'key';
export const NEW_TEMPLATE_CHANNEL_PARAM = 'channel';

export const ROUTES = { ...PUBLIC_ROUTES, ...PRIVATE_ROUTES } as const;

export type PublicRoutePath = (typeof PUBLIC_ROUTES)[keyof typeof PUBLIC_ROUTES];
export type PrivateRoutePath = (typeof PRIVATE_ROUTES)[keyof typeof PRIVATE_ROUTES];
export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * The query parameter `ProtectedRoute` uses to remember where the operator was
 * headed before being bounced to the login page.
 */
export const NEXT_PARAM = 'next';
